import React from 'react';
import '../../../assets/styles/PrivacyPolicy.css';
import Navbar from '../../../components/AuctionFeatureComponents/Home/Navbar';

const PrivacyPolicy = () => {
  return (
    <>
      <Navbar />
      <div className="container-fluid mt-5 pt-5">
        <div className="row justify-content-center">
          <div className="col-md-10">
            <div className="privacy-policy-content">
              <h1 className="text-center mb-5">Privacy Policy</h1>
              <p className="text-muted mb-4">
                <strong>Last Updated: September 29, 2025</strong>
              </p>

              <p>This Privacy Policy describes our policies and procedures on the collection, use, and disclosure of your information when you use the MyLiveAuction platform and tells you about your privacy rights and how the law protects you.</p>

              <p>We use your personal data to provide and improve the Service. By using MyLiveAuction, you agree to the collection and use of information in accordance with this Privacy Policy.</p>

              <h2>Interpretation and Definitions</h2>
              <h3>Interpretation</h3>
              <p>The words with initial capital letters have meanings defined under the following conditions. These definitions have the same meaning whether they appear in singular or plural.</p>

              <h3>Definitions</h3>
              <p>For the purposes of this Privacy Policy:</p>
              <ul>
                <li><strong>Account</strong> means a unique account created for you to access our Service or parts of our Service.</li>
                <li><strong>Company</strong> (referred to as either "the Company", "We", "Us", or "Our") refers to MyLiveAuction, operated by Edmonton Chargers Cricket Club, Edmonton, AB, Canada.</li>
                <li><strong>Cookies</strong> are small files placed on your computer, mobile device, or other device by a website, containing browsing history details and preferences.</li>
                <li><strong>Country</strong> refers to Canada.</li>
                <li><strong>Device</strong> means any device that can access the Service such as a computer, cellphone, or tablet.</li>
                <li><strong>Personal Data</strong> is any information relating to an identified or identifiable individual.</li>
                <li><strong>Service</strong> refers to the MyLiveAuction website and live auction platform.</li>
                <li><strong>Service Provider</strong> means any natural or legal person who processes data on behalf of the Company.</li>
                <li><strong>Usage Data</strong> refers to data collected automatically when using the Service.</li>
                <li><strong>Website</strong> refers to MyLiveAuction, accessible from <a href="https://myliveauction.com">https://myliveauction.com</a></li>
                <li><strong>You</strong> means the individual accessing or using the Service, or the organization or entity on behalf of which such individual is accessing or using the Service.</li>
              </ul>

              <h2>Collecting and Using Your Personal Data</h2>
              <h3>Types of Data Collected</h3>
              <h4>Personal Data</h4>
              <p>While using our Service, we may ask you to provide certain personally identifiable information, including but not limited to:</p>
              <ul>
                <li>Name</li>
                <li>Email address</li>
                <li>Contact number</li>
                <li>Team or organization name</li>
                <li>Sport or tournament details</li>
              </ul>

              <h4>Usage Data</h4>
              <p>Usage Data is collected automatically when using the Service.<br />
              It may include information such as your device's IP address, browser type, browser version, pages visited, time spent, and diagnostic data.</p>

              <p>When accessing via mobile, we may collect information such as device ID, OS version, browser type, and mobile network info.</p>

              <h3>Tracking Technologies and Cookies</h3>
              <p>We use cookies and similar tracking technologies to:</p>
              <ul>
                <li>Maintain user sessions</li>
                <li>Remember preferences</li>
                <li>Improve user experience</li>
                <li>Track usage analytics</li>
              </ul>

              <p>You can configure your browser to refuse cookies. However, some features may not function properly.</p>

              <h4>Types of Cookies We Use:</h4>
              <ul>
                <li><strong>Necessary Cookies (Session):</strong> To enable login, bidding, and auction functionality.</li>
                <li><strong>Preference Cookies (Persistent):</strong> To store language, login, or team info.</li>
                <li><strong>Analytics Cookies:</strong> To understand user behavior and improve performance.</li>
              </ul>

              <h3>Use of Your Personal Data</h3>
              <p>MyLiveAuction may use Personal Data for the following purposes:</p>
              <ul>
                <li>To provide and maintain the Service: Including auction creation, team management, and live bidding.</li>
                <li>To manage your Account: Provide you access to features like auction dashboards, bidding panels, and reports.</li>
                <li>To contact you: For service updates, notifications, or support.</li>
                <li>To manage requests: Respond to inquiries, technical support, or account assistance.</li>
                <li>To improve the platform: Analyze usage, fix bugs, and enhance user experience.</li>
                <li>To send updates or offers: About upcoming tournaments, features, or similar services (optional).</li>
                <li>For compliance: Meet legal obligations or prevent fraud.</li>
              </ul>

              <p>We do not sell or trade your personal information.</p>

              <h3>Retention of Your Personal Data</h3>
              <p>We retain personal data only as long as necessary to:</p>
              <ul>
                <li>Provide our services</li>
                <li>Fulfill legal or accounting obligations</li>
                <li>Resolve disputes</li>
              </ul>

              <p>Usage Data may be retained for analysis and service improvement.</p>

              <h3>Transfer of Your Personal Data</h3>
              <p>Your information may be processed outside your jurisdiction. By using MyLiveAuction, you consent to such transfers, provided adequate security measures are in place.</p>

              <h3>Delete Your Personal Data</h3>
              <p>You can request deletion or correction of your data by:</p>
              <ul>
                <li>Logging into your account and updating settings</li>
                <li>Contacting us at <b>contact.myliveauction@gmail.com</b></li>
              </ul>

              <p>We may retain minimal data if required by law.</p>

              <h3>Disclosure of Your Personal Data</h3>
              <h4>Business Transactions</h4>
              <p>If MyLiveAuction is involved in a merger, acquisition, or asset sale, your data may be transferred. You'll be notified before any transfer occurs.</p>

              <h4>Law Enforcement</h4>
              <p>We may disclose your data if required by law or court order.</p>

              <h4>Other Legal Requirements</h4>
              <p>We may disclose data to:</p>
              <ul>
                <li>Comply with legal obligations</li>
                <li>Protect our rights or users</li>
                <li>Prevent fraud or misuse</li>
                <li>Ensure platform integrity</li>
              </ul>

              <h3>Security of Your Personal Data</h3>
              <p>We use secure protocols (HTTPS), encryption, and access controls. However, no online transmission is 100% secure. You're responsible for maintaining account confidentiality.</p>

              <h3>Children's Privacy</h3>
              <p>Our platform is not intended for children under 13.<br />
              We do not knowingly collect data from minors.<br />
              If you believe a child has submitted data, contact us for immediate removal.</p>

              <h3>Links to Other Websites</h3>
              <p>Our website may contain links to external platforms (e.g. YouTube, payment gateways).<br />
              We are not responsible for third-party content or privacy practices.</p>

              <h3>Changes to This Privacy Policy</h3>
              <p>We may update this Privacy Policy periodically.<br />
              Changes will be posted on this page with a new "Last Updated" date.<br />
              Continued use after updates indicates acceptance.</p>

              <h3>Contact Us</h3>
              <p>If you have any questions about this Privacy Policy, please contact us:</p>
              
              <div className="contact-info">
                <p><strong>MyLiveAuction Support Team</strong></p>
                <p>📧 Email: <a href="mailto:contact.myliveauction@gmail.com">contact.myliveauction@gmail.com</a></p>
                <p>🌐 Website: <a href="https://myliveauction.com">https://myliveauction.com</a></p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      {/* <footer className="bg-dark text-white text-center py-4 mt-5">
        <div className="container">
          <p>© 2025 MyLiveAuction. All rights reserved.</p>
          <div className="footer-links">
            <a href="/privacy-policy">Privacy Policy</a> | 
            <a href="/terms-of-service"> Terms of Service</a> |
            <a href="/contact"> Contact Us</a>
          </div>
        </div>
      </footer> */}
    </>
  );
};

export default PrivacyPolicy;